import { BuiltinModuleTag } from "@scayla/api-client-fetch";

import { $t } from "@/i18n";

const LABEL_KEY_BY_BUILTIN_MODULE: Record<BuiltinModuleTag, string> = {
  [BuiltinModuleTag.DOCUMENTS]: "common.label.dossier",
  [BuiltinModuleTag.PROJECTS]: "common.label.projects",
  [BuiltinModuleTag.CONTACTS]: "common.label.contacts",
  [BuiltinModuleTag.EMAILS]: "common.label.emails",
  [BuiltinModuleTag.FILES]: "common.label.filesModule",
  [BuiltinModuleTag.ACCOUNTING]: "common.label.accounting",
  [BuiltinModuleTag.SETTINGS_ACCOUNTING]: "common.label.accountingSettings",
  [BuiltinModuleTag.ASSETS]: "common.label.assets",
  [BuiltinModuleTag.PRODUCTS]: "common.label.products",
};

const COMPACT_LABEL_KEY_BY_BUILTIN_MODULE: Partial<Record<BuiltinModuleTag, string>> = {
  [BuiltinModuleTag.FILES]: "common.label.filesModuleCompact",
};

const RECORD_KIND_BY_BUILTIN_MODULE: Partial<Record<BuiltinModuleTag, string>> = {
  [BuiltinModuleTag.DOCUMENTS]: "dossier",
  [BuiltinModuleTag.PROJECTS]: "project",
  [BuiltinModuleTag.CONTACTS]: "contact",
  [BuiltinModuleTag.EMAILS]: "email",
  [BuiltinModuleTag.FILES]: "attachment",
  [BuiltinModuleTag.ASSETS]: "asset",
  [BuiltinModuleTag.PRODUCTS]: "product",
};

/**
 * i18n key for the kind of record a module holds, for use inside a sentence — "contact", unlike the
 * module label "Contacts". Both parts are pluralized, so translate them with a count.
 *
 * `demonstrative` carries the article as well ("this contact", "diesen Kontakt"): German inflects it
 * by the noun's gender, so it cannot be assembled from the name in the sentence template.
 *
 * Falls back to the generic "record" for modules with no kind of their own.
 */
export function recordKindKey(tag: BuiltinModuleTag | undefined, part: "name" | "demonstrative"): string {
  const slug = (tag && RECORD_KIND_BY_BUILTIN_MODULE[tag]) ?? "record";
  return `common.recordKind.${slug}.${part}`;
}

const BUILTIN_MODULE_BY_SEEDED_NAME: Record<string, BuiltinModuleTag | undefined> = {
  Dossier: BuiltinModuleTag.DOCUMENTS,
  Projects: BuiltinModuleTag.PROJECTS,
  Contacts: BuiltinModuleTag.CONTACTS,
  Emails: BuiltinModuleTag.EMAILS,
  Files: BuiltinModuleTag.FILES,
  Accounting: BuiltinModuleTag.ACCOUNTING,
  "Accounting Settings": BuiltinModuleTag.SETTINGS_ACCOUNTING,
  Assets: BuiltinModuleTag.ASSETS,
  Products: BuiltinModuleTag.PRODUCTS,
};

export function localizeBuiltinModule(tag: BuiltinModuleTag): string {
  const key = LABEL_KEY_BY_BUILTIN_MODULE[tag];
  return key ? $t(key) : tag;
}

/** Shortened label for width-constrained spots; falls back to the full label. */
export function localizeBuiltinModuleCompact(tag: BuiltinModuleTag): string {
  const key = COMPACT_LABEL_KEY_BY_BUILTIN_MODULE[tag];
  return key ? $t(key) : localizeBuiltinModule(tag);
}

export function localizeModuleName(name: string): string {
  const tag = BUILTIN_MODULE_BY_SEEDED_NAME[name];
  return tag ? localizeBuiltinModule(tag) : name;
}
